import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { selectHoveredPropertyGridCellKey } from '../../redux/features/selectors/referenceBarUISelectors.ts';
import {
  selectFocusedPropertyGridCellKey,
  selectPropertyGridCellKeyPendingActivation,
} from '../../redux/features/selectors/uiSelectors.ts';
import { clearPropertyGridCellKeyPendingActivation } from '../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import {
  PropertyGridFocusContext,
  PropertyGridFocusContextValue,
} from './PropertyGridFocusContext.ts';
import { scrollPropertyGridCellIntoView } from './scrollPropertyGridCellIntoView.ts';
import { PropertyGridCellKey } from './types.ts';

type PropertyGridFocusProviderProps = {
  children: React.ReactNode;
};

const focusCellRef = (ref: RefObject<HTMLElement>) => {
  if (!ref.current) return;
  scrollPropertyGridCellIntoView(ref.current);
  ref.current.focus();
};

const activateCellRef = (ref: RefObject<HTMLElement>) => {
  if (!ref.current) return;
  scrollPropertyGridCellIntoView(ref.current);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      ref.current?.click();
    });
  });
};

export const PropertyGridFocusProvider = ({ children }: PropertyGridFocusProviderProps) => {
  const dispatch = useAppDispatch();
  const activeTargetKey = useAppSelector(selectFocusedPropertyGridCellKey);
  const hoveredPropertyGridCellKey = useAppSelector(selectHoveredPropertyGridCellKey);
  const pendingActivationKey = useAppSelector(selectPropertyGridCellKeyPendingActivation);
  const registry = useRef<Map<PropertyGridCellKey, RefObject<HTMLElement>>>(new Map());
  const activeTargetKeyRef = useRef(activeTargetKey);
  const pendingActivationKeyRef = useRef(pendingActivationKey);
  activeTargetKeyRef.current = activeTargetKey;
  pendingActivationKeyRef.current = pendingActivationKey;

  const tryActivateCell = useCallback(
    (cellKey: PropertyGridCellKey) => {
      const ref = registry.current.get(cellKey);
      if (!ref) {
        return false;
      }
      activateCellRef(ref);
      dispatch(clearPropertyGridCellKeyPendingActivation());
      return true;
    },
    [dispatch],
  );

  const registerField = useCallback(
    (cellKey: PropertyGridCellKey, cellRef: RefObject<HTMLElement>) => {
      registry.current.set(cellKey, cellRef);
      if (activeTargetKeyRef.current === cellKey) {
        focusCellRef(cellRef);
      }
      if (pendingActivationKeyRef.current === cellKey) {
        tryActivateCell(cellKey);
      }
    },
    [tryActivateCell],
  );

  const unregisterField = useCallback((cellKey: PropertyGridCellKey) => {
    registry.current.delete(cellKey);
  }, []);

  useEffect(() => {
    if (!activeTargetKey) return;
    const ref = registry.current.get(activeTargetKey);
    if (!ref) return;
    focusCellRef(ref);
  }, [activeTargetKey]);

  useEffect(() => {
    if (!hoveredPropertyGridCellKey || hoveredPropertyGridCellKey === activeTargetKey) return;
    const ref = registry.current.get(hoveredPropertyGridCellKey);
    if (!ref?.current) return;
    scrollPropertyGridCellIntoView(ref.current);
  }, [activeTargetKey, hoveredPropertyGridCellKey]);

  useEffect(() => {
    if (!pendingActivationKey) return;
    tryActivateCell(pendingActivationKey);
  }, [pendingActivationKey, tryActivateCell]);

  const contextValue = useMemo<PropertyGridFocusContextValue>(
    () => ({
      activeTargetKey,
      registerField,
      unregisterField,
    }),
    [activeTargetKey, registerField, unregisterField],
  );

  return (
    <PropertyGridFocusContext.Provider value={contextValue}>
      {children}
    </PropertyGridFocusContext.Provider>
  );
};
